import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { CornerDownLeft, Trash2 } from "lucide-react"
import { AnimatePresence } from "motion/react"
import * as m from "motion/react-m"
import React from "react"
import { QueuedChatMessage } from "./hooks/useChatSubmit"

interface ChatMessageQueueProps {
  queuedMessages: QueuedChatMessage[]
  onRemoveQueuedMessage: (messageId: string) => void
  onForceSendQueuedMessage: (messageId: string) => void
}

export const ChatMessageQueue: React.FC<ChatMessageQueueProps> = ({
  queuedMessages,
  onRemoveQueuedMessage,
  onForceSendQueuedMessage,
}) => {
  const { translate } = useTranslation()
  const nextQueuedMessage = queuedMessages[0]
  const additionalQueuedMessages = Math.max(queuedMessages.length - 1, 0)

  return (
    <AnimatePresence initial={false}>
      {nextQueuedMessage && (
        <m.div
          key={nextQueuedMessage.id}
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="mx-auto -mb-px max-w-[960px]"
        >
          <div className="bg-zinc-900/95 text-zinc-100 overflow-hidden rounded-t-[1.4rem] border-x-2 border-b-0 border-t-2 border-border/80 px-4 py-2.5 shadow-[0_-12px_36px_rgba(15,23,42,0.16)] backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                  {additionalQueuedMessages > 0 && (
                    <span className="bg-white/8 text-zinc-300 shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium">
                      {translate("AI_CHAT/QUEUE/COUNT", {
                        count: additionalQueuedMessages,
                      })}
                    </span>
                  )}
                  <p className="text-zinc-100/90 truncate text-sm font-medium">
                    {nextQueuedMessage.text}
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-zinc-300 hover:bg-white/8 size-8 rounded-full hover:text-white"
                  onClick={() => onRemoveQueuedMessage(nextQueuedMessage.id)}
                  title={translate("AI_CHAT/QUEUE/REMOVE")}
                  aria-label={translate("AI_CHAT/QUEUE/REMOVE")}
                >
                  <Trash2 className="size-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-zinc-300 hover:bg-white/8 size-8 rounded-full hover:text-white"
                  onClick={() => onForceSendQueuedMessage(nextQueuedMessage.id)}
                  title={translate("AI_CHAT/QUEUE/FORCE_SEND")}
                  aria-label={translate("AI_CHAT/QUEUE/FORCE_SEND")}
                >
                  <CornerDownLeft className="size-4" />
                </Button>
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  )
}
